import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api, session } from '../../shell/bridge';
import { loadMembers, type Member } from '../../lib/api';
import { CHORE_SEED_GROUPS } from '../../../../shared/chores-seeds';
import { CHORE_DONE_NOTE_MAX, CHORE_REACTION_GLYPHS, type ChoreReaction } from '../../../../shared/types';
import { Pressable } from '../../ui/Pressable';
import { Switch } from '../../ui/Switch';
import { SkeletonBlock } from '../../ui/Skeleton';
import { Overlay } from '../../ui/Overlay';
import { KudosToast } from '../Requests';
import './chores.css';

/** Row shape as the API serializes it (SQLite 0/1 never reaches the client —
 *  booleans stay booleans, timestamps are numbers). */
interface ChoreItem {
  choreId: string;
  title: string;
  cadence: 'once' | 'recurring';
  freq: 'daily' | 'weekly' | 'monthly' | null;
  /** Which rule rolls the date forward (card VmoORelj1HuO). */
  recurMode: 'fixed' | 'interval' | 'completion';
  /** Step in days for interval / completion, null for fixed. */
  recurEveryDays: number | null;
  assigneeId: string | null;
  /** Who governs the assignee (card UeOwZSHfdZCh): fixed, claim, alternate. */
  assignMode: 'fixed' | 'claim' | 'alternate';
  dueAt: number | null;
  status: 'open' | 'done';
  completedAt: number | null;
  completedBy: string | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** False when it was created with the "Add to Calendar & Tasks" switch off. */
  syncGoogle: boolean;
  /** Times this occurrence has been pushed out (snooze or defer) — 3 is the
   *  cap, after which the panel offers a change of plan instead (xvom08w4BgGT). */
  snoozeCount: number;
  /** Public reactions on this card (MnorjNDwB3of) — at most one per member. */
  reactions: ChoreReaction[];
  /** The warm receipt left at completion ("done — out of bin liners"). */
  doneNote: string | null;
  /** Who left the receipt — completer or partner. */
  doneNoteBy: string | null;
}

/** The couple's list-clear streak (GET /api/chores/streak). Couple-scoped:
 *  no member id is ever part of this shape. */
interface ChoreStreak {
  streak: number;
  lastClearWeek: number | null;
  lastClearAt: number | null;
}

/** The three pushes the "Later" chooser offers, as offsets from now. Each is
 *  a real time rather than a date string, so the worker never has to guess a
 *  timezone — and every one of them is safely in the future. */
const SNOOZE_CHOICES: { key: string; label: string; ms: number }[] = [
  { key: 'tomorrow', label: 'Tomorrow', ms: 24 * 3_600_000 },
  { key: 'in3', label: 'In 3 days', ms: 3 * 24 * 3_600_000 },
  { key: 'nextweek', label: 'Next week', ms: 7 * 24 * 3_600_000 },
];

/** error.code out of the JSON body `api()` throws — null when the message is
 *  not parseable JSON, so a network failure never reads as a code. */
function errCode(e: unknown): string | null {
  try {
    const parsed = JSON.parse((e as Error)?.message ?? '');
    return parsed?.error?.code ?? null;
  } catch {
    return null;
  }
}

/** GET /api/chores/google/status — configured tells the panel whether this
 *  worker has a Google client at all; tokens are never part of this shape. */
interface GoogleStatus {
  configured: boolean;
  connected: boolean;
  provider: string | null;
  accountEmail: string | null;
  scopes: string[];
  connectedAt: number | null;
}

const CADENCE_OPTIONS: { value: 'once' | 'recurring'; label: string }[] = [
  { value: 'once', label: '1x' },
  { value: 'recurring', label: 'Recurring' },
];

const FREQ_OPTIONS: { value: string; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

/** The three rules a recurring chore can carry (card VmoORelj1HuO): the
 *  classic fixed cadence, a straight N-day interval kept on schedule, and a
 *  step counted from the tick itself. */
type RecurMode = 'fixed' | 'interval' | 'completion';

const RECUR_OPTIONS: { value: RecurMode; label: string }[] = [
  { value: 'fixed', label: 'On a cadence' },
  { value: 'interval', label: 'Every N days' },
  { value: 'completion', label: 'After it is done' },
];

/** What the row's cadence chip says: the rule in the words a user picked. */
function recurChip(chore: ChoreItem): string {
  if (chore.cadence !== 'recurring') return '1x';
  const mode = chore.recurMode ?? 'fixed';
  if (mode === 'interval') return `↺ every ${chore.recurEveryDays ?? 1}d`;
  if (mode === 'completion') return `↺ ${chore.recurEveryDays ?? 1}d after done`;
  return `↺ ${chore.freq ?? 'weekly'}`;
}

/** Epoch ms -> the `YYYY-MM-DD` an <input type="date"> holds, in local time
 *  (the same wall clock the form stamps on the way out). */
function toDateInput(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Epoch ms -> the `HH:MM` an <input type="time"> holds; local noon is the
 *  date-only sentinel, so it reads as "no time picked". */
function toTimeInput(ms: number | null): string {
  if (ms == null) return '';
  const d = new Date(ms);
  if (d.getHours() === 12 && d.getMinutes() === 0) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Who the create form assigns the chore to. The row's own pill cycles the
 *  same three states afterwards; this is the choice made BEFORE it exists. */
type AssigneeChoice = 'me' | 'partner' | 'anyone';

/** How the row decides who is on it (card UeOwZSHfdZCh). */
type AssignMode = 'fixed' | 'claim' | 'alternate';

const ASSIGN_OPTIONS: { value: AssignMode; label: string }[] = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'claim', label: 'Claim' },
  { value: 'alternate', label: 'Alternate' },
];

/** What the mode says in one line — shown under the chips, because a mode
 *  nobody can read is a mode nobody will pick. */
const ASSIGN_HINT: Record<AssignMode, string> = {
  fixed: 'Whoever you put on it stays on it.',
  claim: 'Nobody until they take it — first tap wins.',
  alternate: 'Whoever finishes hands it to the other one.',
};

function dueLabel(chore: ChoreItem): string {
  if (chore.dueAt == null) return 'No date';
  const at = new Date(chore.dueAt);
  // Local noon is the date-only sentinel the date picker has always sent
  // (`T12:00:00`), so a row without a picked time still reads as a date and
  // one with a time shows the clock next to it.
  const hasTime = at.getHours() !== 12 || at.getMinutes() !== 0;
  // Spelled month, never the locale's `12/11/26`: a numeric date reads as
  // either day-first or month-first depending on who is looking, and that
  // ambiguity is what makes a correct row look like a wrong one (founder
  // report 2026-10-08).
  const day = at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (!hasTime) return day;
  return `${day} · ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

/** The six reaction chips draw Lucide vectors now, not emoji (card bFaRParNQH98,
 *  Jonathan 2026-10-23: emoji render per-platform and sit outside the vector set
 *  the rest of the app draws). Shapes are inlined from lucide-static v1.61.0 with
 *  no icon dependency — the same reason Thread inlines sparkles / tic-tac-toe:
 *  a package would mix stroke weights with the shapes already in the tree.
 *
 *  The WIRE is untouched: CHORE_REACTION_GLYPHS stays emoji and
 *  POST /api/chores/:id/react still sends the glyph, so every reaction already
 *  stored keeps rendering. What leaves the DOM is the emoji itself — the chip
 *  carries `data-reaction` and a spoken word instead, which is what the walk
 *  asserts and what screen readers read. (lucide has no open-mouth-only face at
 *  1.61.0, so the 😮 slot draws `laugh`, the nearest open-mouth shape.) */
const REACTION_SHAPES: Record<string, { name: string; label: string; body: ReactNode }> = {
  '👏': {
    name: 'clap',
    label: 'clap',
    body: (
      <>
        <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
        <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
        <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
      </>
    ),
  },
  '❤️': {
    name: 'heart',
    label: 'heart',
    body: (
      <path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5" />
    ),
  },
  '🙌': {
    name: 'hands',
    label: 'raised hands',
    body: (
      <>
        <path d="M11 14h2a2 2 0 0 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16" />
        <path d="m14.45 13.39 5.05-4.694C20.196 8 21 6.85 21 5.75a2.75 2.75 0 0 0-4.797-1.837.276.276 0 0 1-.406 0A2.75 2.75 0 0 0 11 5.75c0 1.2.802 2.248 1.5 2.946L16 11.95" />
        <path d="m2 15 6 6" />
        <path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a1 1 0 0 0-2.75-2.91" />
      </>
    ),
  },
  '🔥': {
    name: 'fire',
    label: 'fire',
    body: (
      <path d="M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4" />
    ),
  },
  '😮': {
    name: 'wow',
    label: 'wow',
    body: (
      <>
        <path d="M15 10V9" />
        <path d="M7.084 14.302a5.12 5.12 0 0 0 9.833 0 .24.24 0 0 0-.235-.302H7.32a.24.24 0 0 0-.235.302" />
        <path d="M9 10V9" />
        <circle cx="12" cy="12" r="10" />
      </>
    ),
  },
  '🎉': {
    name: 'party',
    label: 'party',
    body: (
      <>
        <path d="M5.8 11.3 2 22l10.7-3.79" />
        <path d="M4 3h.01" />
        <path d="M22 8h.01" />
        <path d="M15 2h.01" />
        <path d="M22 20h.01" />
        <path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10" />
        <path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17" />
        <path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7" />
        <path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z" />
      </>
    ),
  },
};

/**
 * Chores panel — the seed list the Life screen's "Chores" tile promised.
 * One row per chore: cadence chip (1x vs ↺ recurring), an assignee pill that
 * cycles me → partner → anyone, the date it is due (or next due, for the
 * recurring ones), and a tick-off that celebrates with the same toast the
 * kudos card uses.
 */
export function Chores() {
  const me = session();
  const [items, setItems] = useState<ChoreItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [title, setTitle] = useState('');
  const [cadence, setCadence] = useState<'once' | 'recurring'>('once');
  const [freq, setFreq] = useState('weekly');
  const [due, setDue] = useState('');
  /** Optional clock time for the due date — the "Specify Time" field. Empty
   *  keeps the date-only noon sentinel the picker has always sent. */
  const [time, setTime] = useState('');
  /** Assignee chosen at creation: the Me / Partner / Anyone segments. */
  const [assignee, setAssignee] = useState<AssigneeChoice>('anyone');
  /** Which rule owns that assignee (card UeOwZSHfdZCh). */
  const [assignMode, setAssignMode] = useState<AssignMode>('fixed');
  /** Has a person actually chosen a mode this time round? A NEW recurring
   *  chore defaults to 'alternate' — turns need no fairness engine, and the
   *  cheapest fairness win is drift that never starts (card kZX3gsJkKaTH).
   *  The default follows the cadence only until someone picks a mode by hand,
   *  and an edit always counts as picked, so opening an existing chore never
   *  rewrites the rule it was created with. */
  const [assignModePicked, setAssignModePicked] = useState(false);
  /** Which rule a recurring chore follows (card VmoORelj1HuO). */
  const [recurMode, setRecurMode] = useState<RecurMode>('fixed');
  /** Step, in days, for the two N-day rules — held as the field's text so a
   *  half-typed "1" is editable state, not an invalid number. */
  const [every, setEvery] = useState('3');
  /** Seeds picked in the library: several chores can be added in one go. */
  const [picked, setPicked] = useState<string[]>([]);
  /** Row being edited — the composer switches to Save / Cancel while it is
   *  set, so edit reuses every field the create form already has. */
  const [editing, setEditing] = useState<ChoreItem | null>(null);
  /** Delete is two taps: first arms the row, second removes it. */
  const [armedDelete, setArmedDelete] = useState<string | null>(null);
  /** "Add to Calendar & Tasks" — on by default, only offered on builds that
   *  have a Google client configured (the switch is hidden otherwise). */
  const [addToGoogle, setAddToGoogle] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Chore that just got ticked off — drives the one-shot celebration toast. */
  const [celebrated, setCelebrated] = useState<string | null>(null);
  /* Week summary drawer (card QR9phRSRQHNx, founder batch 2026-10-13):
     day | task | responsible | complete for the week ahead, as a right-edge
     pop-out. The three filters are panel state like every other drawer. */
  const [weekOpen, setWeekOpen] = useState(false);
  const [weekDay, setWeekDay] = useState('all');
  const [weekWho, setWeekWho] = useState('all');
  const [weekDone, setWeekDone] = useState('all');
  /* Desktop uplift A6 (card R0Yth1PtWGRg): at the shipped >=1100px token the
     week summary is a persistent right rail beside the list — same matchMedia
     seam Thread, A2 and A3 use. Below the token none of it exists: the
     toggle, the drawer and the phone box tree are the ones that shipped. */
  const [wide, setWide] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1100px)').matches);
  /** Every member of the couple (id AND display name — the create form's
   *  segments name the partner). Read from the session cache FIRST, then
   *  refreshed — a solo couple (partner has not joined yet) must still cycle
   *  Anyone ↔ Me rather than dead-end on a tap that goes nowhere (card
   *  4kQn1aaw0rf9). */
  const [members, setMembers] = useState<Member[]>([]);

  /* Reactions + done-notes (card MnorjNDwB3of): one glyph per member, one
     receipt line per completion. The composer is row-scoped — which card is
     getting a note and the draft riding it — because a note attaches to one
     completion, never to the panel. */
  /** Row whose done-note composer is open. */
  const [noteFor, setNoteFor] = useState<string | null>(null);
  /** The draft line for that row. */
  const [noteDraft, setNoteDraft] = useState('');
  /** Set while a react/note POST is in flight — keyed to the row. */
  const [reactBusyId, setReactBusyId] = useState<string | null>(null);

  /* Lifecycle (card xvom08w4BgGT): the "Later" chooser, the cap that stops a
     fourth push, and the couple's list-clear streak. All three are panel
     state, never row state — the chooser and the options card are about the
     list, and the streak has no owner but the couple. */
  /** Row whose Later chooser is open. */
  const [laterFor, setLaterFor] = useState<string | null>(null);
  /** The chore the cap stopped — drives the Reassign / Renegotiate card. */
  const [capped, setCapped] = useState<string | null>(null);
  /** One line of feedback for the chooser / options card. */
  const [laterNote, setLaterNote] = useState<string | null>(null);
  /** The streak celebration line, set once and never by a plain tick-off. */
  const [streakLabel, setStreakLabel] = useState<string | null>(null);
  /** The last clear this panel has already acknowledged. Primed on mount so a
   *  streak fired before the panel opened is history, not news — the toast is
   *  an edge, not a level. */
  const seenClearAt = useRef<number | null>(null);
  const streakPrimed = useRef(false);

  /* Google Calendar + Tasks connect (card hWnpJn7Tfl38) — status, the
     one-shot notice Google hands back, and the two buttons that drive it. */
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [gBusy, setGBusy] = useState(false);
  const [gNote, setGNote] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await api<{ chores: ChoreItem[] }>('/api/chores');
      setItems(data.chores ?? []);
      setFailed(false);
    } catch (e) {
      console.error('[Pear] chores load failed', e);
      setItems([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Live from the room socket: when the partner taps a reaction or writes a
  // done-note, this list refetches so the glyph and receipt land without a
  // reload (card MnorjNDwB3of) — the same hand-off the Requests panel uses
  // for kudos (the DO broadcasts t:'chores'; useThread turns it into this).
  useEffect(() => {
    const onChores = () => void reload();
    window.addEventListener('pear:chores', onChores);
    return () => window.removeEventListener('pear:chores', onChores);
  }, [reload]);

  // The toast is anchored to the viewport above the tab bar (card
  // FktjQ7CT4keo), so the row it celebrates is brought to the middle of the
  // panel after the list reloads — the card the toast is about is never the
  // card sitting underneath it. Re-runs when `items` lands: the Done row only
  // exists once that reload resolves.
  useEffect(() => {
    if (!celebrated) return;
    const row = document.querySelector(`[data-chore-id="${celebrated}"]`);
    if (!row) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
  }, [celebrated, items]);

  // Who is "partner" on the assignee pill. Read once — a member list does not
  // change while the panel is open, and a refetch loop would fight the clicks.
  // Keyed on memberId, not `me`: session() hands back a fresh object every
  // render, so `me` as a dependency re-fired this effect forever and kept
  // re-posting /api/pair/status (same root cause as card iKkKUEi2QZHZ).
  const meKey = me?.memberId ?? '';
  useEffect(() => {
    if (!meKey) return;
    let live = true;
    // The cache is enough to name the other member on the spot: the refresh
    // below only corrects it, and a failed refresh must not take the control
    // away (that is the bug this card reports).
    const cached = loadMembers();
    if (cached.length) setMembers(cached);
    api<{ couple?: { members?: Member[] } }>('/api/pair/status')
      .then(d => {
        if (!live) return;
        const ms = d.couple?.members ?? [];
        if (ms.length) setMembers(ms);
      })
      .catch(() => {/* keep whatever the cache gave us */});
    return () => { live = false; };
  }, [meKey]);

  /* Escape closes the week drawer the way it closes every other overlay. */
  useEffect(() => {
    if (!weekOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setWeekOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [weekOpen]);

  /* The token is a live seam (same contract A2/A3 ship): resizing across it
     swaps the drawer for the rail. A drawer left open at the moment of the
     cross would strand an overlay the toggle no longer controls, so the
     cross closes it — the rail it becomes is already on screen. */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1100px)');
    const sync = () => {
      setWide(mq.matches);
      if (mq.matches) setWeekOpen(false);
    };
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const loadGoogle = useCallback(async () => {
    try {
      setGoogle(await api<GoogleStatus>('/api/chores/google/status'));
    } catch (e) {
      console.error('[Pear] google connect status failed', e);
      setGoogle(null);
    }
  }, []);

  useEffect(() => {
    void loadGoogle();
  }, [loadGoogle]);

  // Land back from Google: read ?gconnect= once, then take it off the URL so
  // a refresh does not repeat the same notice (same pattern as ?oauth_code=).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('gconnect');
    if (!result) return;
    setGNote(
      result === 'connected'
        ? 'Google connected — chores can now reach that calendar and task list.'
        : result === 'denied'
          ? 'Google link cancelled — nothing was shared.'
          : result === 'expired'
            ? 'That Google link expired — press Connect to start again.'
            : 'The Google Link Did Not Complete — Press Connect to Start Again.',
    );
    params.delete('gconnect');
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }, []);

  const connectGoogle = async () => {
    if (gBusy) return;
    setGBusy(true);
    try {
      const res = await api<{ url: string }>('/api/chores/google/connect', {
        body: { return: window.location.origin },
      });
      if (!res.url) throw new Error('no authorize url');
      // Top-level navigation on purpose: the consent screen is Google's page,
      // not ours, and it redirects straight back to the worker's callback.
      window.location.assign(res.url);
    } catch (e) {
      console.error('[Pear] google connect start failed', e);
      setGNote('Could not start the Google link — try again in a moment.');
      setGBusy(false);
    }
  };

  const disconnectGoogle = async () => {
    if (gBusy) return;
    setGBusy(true);
    try {
      await api('/api/chores/google/disconnect', { body: {} });
      setGNote('Google disconnected — Pear no longer touches that calendar.');
      await loadGoogle();
    } catch (e) {
      console.error('[Pear] google disconnect failed', e);
    } finally {
      setGBusy(false);
    }
  };

  const celebrate = (choreId: string) => {
    setCelebrated(choreId);
    window.setTimeout(
      () => setCelebrated(c => (c === choreId ? null : c)),
      4000,
    );
  };

  // An armed delete disarms itself: "Sure?" is a moment, not a mode, so a
  // row left armed cannot be destroyed by the next stray tap minutes later.
  useEffect(() => {
    if (!armedDelete) return;
    const t = window.setTimeout(() => setArmedDelete(null), 4000);
    return () => window.clearTimeout(t);
  }, [armedDelete]);

  /** The other member, if the couple has one — the create form's segments
   *  name them, and the row's own pill still says "Partner". Solo couples
   *  simply have no partner segment to pick. */
  const otherMember = members.find(m => m.memberId !== (me?.memberId ?? '')) ?? null;
  const partnerName = otherMember?.displayName?.trim() || 'Partner';

  /** The N-day step as a usable day count, or null while the field holds
   *  something the API would refuse (0, 4000, half a number). */
  const everyDays = (() => {
    const n = Number(every);
    return Number.isInteger(n) && n >= 1 && n <= 365 ? n : null;
  })();

  /** What the composer is currently describing, as the API wants it. */
  const composerBody = (): Record<string, unknown> | null => {
    const t = title.trim();
    if (!t) return null;
    if (cadence === 'recurring' && recurMode !== 'fixed' && everyDays === null) return null;
    const body: Record<string, unknown> = { title: t, cadence };
    if (cadence === 'recurring') {
      if (recurMode === 'fixed') body.freq = freq;
      else {
        body.recurMode = recurMode;
        body.recurEveryDays = everyDays;
      }
    }
    // Noon stays the date-only sentinel; a picked time rides along on the
    // same timestamp, and hasTime tells the worker WHICH of the two it is —
    // the sentinel is local noon, so only this browser can recognise it and
    // the worker must not guess (card ZgLp6PjCXHEm: a time-blocked chore is
    // a Calendar event, a dated one is a Tasks task).
    // Only a real picker date (`YYYY-MM-DD`) reaches the API: without a date
    // picker the field falls back to free text, and a typed `12/11/26` used
    // to parse into a wrong-but-plausible date. The Add button is already
    // disabled when a time is set with no date, so this is the second net.
    if (/^\d{4}-\d{2}-\d{2}$/.test(due)) {
      const pickedTime = Boolean(time) && /^\d{2}:\d{2}$/.test(time);
      const stamped = new Date(pickedTime ? `${due}T${time}:00` : `${due}T12:00:00`).getTime();
      if (!Number.isNaN(stamped)) {
        body.dueAt = stamped;
        body.hasTime = pickedTime;
      }
    }
    body.assignMode = assignMode;
    // A claim chore is open until someone takes it, and an edit must never
    // release a claim somebody already holds — so the field is left OUT of
    // the request instead of being sent as null (card UeOwZSHfdZCh). The
    // Me / Partner / Anyone row is hidden for that mode anyway.
    if (assignMode !== 'claim') {
      body.assigneeId =
        assignee === 'me' ? (me?.memberId ?? null)
          : assignee === 'partner' ? (otherMember?.memberId ?? null)
            : null;
    }
    if (!editing) body.syncGoogle = addToGoogle;
    return body;
  };

  const resetComposer = () => {
    setTitle('');
    setDue('');
    setTime('');
    setEditing(null);
    setRecurMode('fixed');
    setEvery('3');
    setAssignMode('fixed');
    setAssignModePicked(false);
  };

  /** Add a row, or save the one being edited (card VmoORelj1HuO). */
  const submitChore = async (e: FormEvent) => {
    e.preventDefault();
    const body = composerBody();
    if (!body || adding) return;
    if (editing) {
      // A cleared date is a real edit: the row goes back to "No date" rather
      // than keeping whatever it had. syncGoogle has no edit path by design
      // (the switch is part of the create form), so it is never sent here.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) {
        body.dueAt = null;
        body.hasTime = false;
      }
      delete body.syncGoogle;
    }
    setAdding(true);
    try {
      if (editing) await api(`/api/chores/${editing.choreId}`, { method: 'PATCH', body });
      else await api('/api/chores', { body });
      resetComposer();
      await reload();
    } catch (err) {
      console.error(editing ? '[Pear] edit chore failed' : '[Pear] add chore failed', err);
    } finally {
      setAdding(false);
    }
  };

  /** A seed chip toggles: several can be picked before one Add. */
  const toggleSeed = (seed: string) => {
    setPicked(p => (p.includes(seed) ? p.filter(s => s !== seed) : [...p, seed]));
  };

  /** Add every picked seed as its own one-off chore, under the assignee the
   *  composer already chose — that is what makes a whole week's shelf a
   *  three-tap job instead of fourteen forms. */
  const addSeeds = async () => {
    if (picked.length === 0 || adding) return;
    setAdding(true);
    const assigneeId =
      assignee === 'me' ? (me?.memberId ?? null)
        : assignee === 'partner' ? (otherMember?.memberId ?? null)
          : null;
    try {
      for (const t of picked) {
        // The seeds take the composer's mode too (card UeOwZSHfdZCh) — a
        // claim shelf starts open, a fixed shelf takes the person picked.
        const body: Record<string, unknown> = { title: t, cadence: 'once', assignMode, syncGoogle: addToGoogle };
        if (assignMode !== 'claim') body.assigneeId = assigneeId;
        await api('/api/chores', { body });
      }
      setPicked([]);
      await reload();
    } catch (err) {
      console.error('[Pear] add seeds failed', err);
      await reload();
    } finally {
      setAdding(false);
    }
  };

  /** Load a row into the composer so its fields can be changed. */
  const beginEdit = (chore: ChoreItem) => {
    setEditing(chore);
    setTitle(chore.title);
    setCadence(chore.cadence);
    setFreq(chore.freq ?? 'weekly');
    setRecurMode(chore.recurMode ?? 'fixed');
    setEvery(String(chore.recurEveryDays ?? 3));
    setDue(toDateInput(chore.dueAt));
    setTime(toTimeInput(chore.dueAt));
    setAssignMode(chore.assignMode ?? 'fixed');
    setAssignModePicked(true);
    setAssignee(
      chore.assigneeId == null ? 'anyone'
        : chore.assigneeId === (me?.memberId ?? '') ? 'me'
          : 'partner',
    );
    setArmedDelete(null);
    const input = document.getElementById('ch-input');
    input?.scrollIntoView({ block: 'center' });
    (input as HTMLInputElement | null)?.focus();
  };

  /** Two taps to remove a row: the first arms it and says so, the second
   *  means it. A stray tap must never delete someone's chore. */
  const removeChore = async (chore: ChoreItem) => {
    if (busyId) return;
    if (armedDelete !== chore.choreId) {
      setArmedDelete(chore.choreId);
      return;
    }
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}`, { method: 'DELETE' });
      setArmedDelete(null);
      if (editing?.choreId === chore.choreId) resetComposer();
      await reload();
    } catch (err) {
      console.error('[Pear] delete chore failed', err);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Assignee pill cycles me → partner → anyone: one control, three states.
   *  Solo (no second member yet) it is a two-state cycle, me ↔ anyone — every
   *  tap must move the pill, so the assignment always sticks.
   *
   *  The row's own mode decides what a tap MEANS (card UeOwZSHfdZCh): fixed
   *  and alternate cycle the pointer, claim takes the row when it is free,
   *  lets go of a hold that is yours, and does nothing when the partner
   *  holds it — a claim is theirs to release, never to be taken back. */
  const tapAssignee = async (chore: ChoreItem) => {
    if (busyId || !me) return;
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'claim') {
      if (chore.assigneeId != null && chore.assigneeId !== me.memberId) return;
      setBusyId(chore.choreId);
      try {
        if (chore.assigneeId == null) await api(`/api/chores/${chore.choreId}/claim`, { body: {} });
        else await api(`/api/chores/${chore.choreId}/assign`, { body: { assigneeId: null } });
        await reload();
      } catch (err) {
        console.error('[Pear] claim chore failed', err);
        await reload();
      } finally {
        setBusyId(null);
      }
      return;
    }
    const other = members.find(m => m.memberId !== me.memberId)?.memberId ?? null;
    const next = chore.assigneeId == null
      ? me.memberId
      : chore.assigneeId === me.memberId
        ? other // null while solo — drop straight back to Anyone
        : null;
    if (next === chore.assigneeId) return;
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/assign`, { body: { assigneeId: next } });
      await reload();
    } catch (err) {
      console.error('[Pear] assign chore failed', err);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const complete = async (chore: ChoreItem) => {
    if (busyId) return;
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/complete`, { body: {} });
      celebrate(chore.choreId);
      await reload();
      // Only a completion can clear the list, so this is the only call site
      // that can turn a streak into a celebration (card xvom08w4BgGT).
      await checkStreak();
    } catch (e) {
      console.error('[Pear] complete chore failed', e);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Tap a reaction glyph (card MnorjNDwB3of) — a toggle: the same glyph
   *  again clears it, a different one replaces it. One per member, so the
   *  pair's two taps are the whole thread. Any member may react to any row. */
  const react = async (chore: ChoreItem, glyph: string) => {
    if (reactBusyId) return;
    setReactBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/react`, { body: { glyph } });
      await reload();
    } catch (e) {
      console.error('[Pear] chore reaction failed', e);
    } finally {
      setReactBusyId(null);
    }
  };

  /** Save the warm receipt (card MnorjNDwB3of). Whether the note rides the
   *  tick or replaces an existing one is decided by COMPLETION STATE, not by
   *  `status` — a recurring chore stays 'open' forever after a tick, so
   *  branching on status would tick it AGAIN (advancing its date and
   *  rotating its assignee) just to edit last round's note (sol review
   *  2026-10-16). A row that has never been completed completes WITH the
   *  note; anything already ticked just replaces the stored line. */
  const saveNote = async (chore: ChoreItem) => {
    if (busyId || reactBusyId) return;
    const note = noteDraft.trim();
    setBusyId(chore.choreId);
    try {
      if (chore.completedAt == null) {
        await api(`/api/chores/${chore.choreId}/complete`, { body: { note } });
        celebrate(chore.choreId);
      } else {
        await api(`/api/chores/${chore.choreId}/done-note`, { body: { note } });
      }
      setNoteFor(null);
      setNoteDraft('');
      await reload();
    } catch (e) {
      console.error('[Pear] chore done-note failed', e);
    } finally {
      setBusyId(null);
    }
  };

  /** Who left the receipt, in the panel's own words (Me / Partner). */
  const noteByLabel = (chore: ChoreItem): string => {
    if (!chore.doneNoteBy) return '';
    if (me && chore.doneNoteBy === me.memberId) return 'Me';
    return 'Partner';
  };

  /** The reaction strip + done-note receipt a card carries (card
   *  MnorjNDwB3of). Rendered on BOTH the open and done rows, because a
   *  reaction is warm on work in progress and on work finished. The glyph
   *  row is a closed set — there is no text field, so there is nothing to
   *  reply to and no thread can form. The receipt line shows who left it.
   *
   *  Both partners may pick the SAME glyph, so "is this mine" is answered by
   *  member, never by glyph — a `.find()` by glyph would return only the
   *  first entry and hide the second partner's own tap. Occupancy and my own
   *  selection are derived independently, and the count is shown so two
   *  reactions never masquerade as one (sol review 2026-10-16). */
  const renderReactions = (chore: ChoreItem) => {
    return (
      <div className="ch-social">
        <div className="ch-react" role="group" aria-label={`React to ${chore.title}`}>
          {CHORE_REACTION_GLYPHS.map(glyph => {
            const hits = chore.reactions.filter(r => r.glyph === glyph);
            const byMe = !!me && hits.some(r => r.memberId === me.memberId);
            // The wire still carries the emoji glyph (CHORE_REACTION_GLYPHS +
            // POST /react, unchanged); only the RENDER is a vector now, so the
            // strip reads as line art like the rest of the app and no emoji
            // codepoint ships in the DOM (card bFaRParNQH98). The shape also
            // supplies the chip's spoken name, so screen readers say "fire",
            // never "emoji fire". The glyph fallback exists only for a future
            // glyph added to the shared list without a shape here.
            const shape = REACTION_SHAPES[glyph];
            const spoken = shape?.label ?? glyph;
            return (
              <Pressable
                key={glyph}
                data-reaction={shape?.name}
                className={`ch-react-chip${hits.length ? ' is-on' : ''}${byMe ? ' is-mine' : ''}`}
                disabled={reactBusyId === chore.choreId}
                aria-pressed={byMe}
                aria-label={`React ${spoken} to ${chore.title}${hits.length ? `, ${hits.length} reaction${hits.length === 1 ? '' : 's'}` : ''}`}
                onClick={() => void react(chore, glyph)}
              >
                {shape ? (
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {shape.body}
                  </svg>
                ) : (
                  glyph
                )}
                {hits.length > 1 && <span className="ch-react-count">{hits.length}</span>}
              </Pressable>
            );
          })}
        </div>
        {chore.doneNote && (
          <p className="ch-receipt" data-testid="chore-receipt">
            <span className="ch-receipt-note">“{chore.doneNote}”</span>
            {noteByLabel(chore) && <span className="ch-receipt-by"> — {noteByLabel(chore)}</span>}
          </p>
        )}
      </div>
    );
  };

  /** Open the receipt composer on a row, seeded with whatever note it
   *  already carries so a replace starts from the current line (MnorjNDwB3of). */
  const openNote = (chore: ChoreItem) => {
    setNoteFor(chore.choreId);
    setNoteDraft(chore.doneNote ?? '');
  };

  /** The receipt composer itself (card MnorjNDwB3of). One line, one Save —
   *  a receipt, not a thread: there is no reply, no history, no @mention.
   *  On an open row the Save also ticks the chore off (a receipt needs a
   *  completion to receipt); on a done row it just replaces the line. */
  const renderNoteComposer = (chore: ChoreItem) => {
    if (noteFor !== chore.choreId) return null;
    // The save rides the SAME per-row lock as Tick off (busyId) — the two
    // completion paths must exclude each other, so the spinner reads that
    // lock, not the reaction one (sol review 2026-10-16).
    const saving = busyId === chore.choreId;
    return (
      <div className="ch-note-composer" role="group" aria-label={`Add a note to ${chore.title}`}>
        <input
          className="pear-chat-input ch-note-input"
          data-testid="chore-note-input"
          value={noteDraft}
          maxLength={CHORE_DONE_NOTE_MAX}
          placeholder="done — out of bin liners"
          aria-label={`Done note for ${chore.title}`}
          onChange={e => setNoteDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void saveNote(chore);
            if (e.key === 'Escape') { setNoteFor(null); setNoteDraft(''); }
          }}
        />
        <Pressable
          className="pear-btn pear-btn-mustard ch-note-save"
          disabled={saving}
          aria-label={`Save note on ${chore.title}`}
          onClick={() => void saveNote(chore)}
        >
          {saving ? '…' : chore.status === 'open' ? 'Tick off + note' : 'Save note'}
        </Pressable>
        <Pressable
          className="pear-btn ch-note-cancel"
          disabled={saving}
          aria-label={`Cancel note on ${chore.title}`}
          onClick={() => { setNoteFor(null); setNoteDraft(''); }}
        >
          Cancel
        </Pressable>
      </div>
    );
  };

  /** Read the couple's streak and celebrate a clear this panel has not seen.
   *  The first read only primes the ref: a streak that fired earlier in the
   *  week is history, and re-announcing it on every visit would be the nag
   *  this card exists to avoid. Never throws — a celebration is not a
   *  dependency, and a failed read must not break a tick-off. */
  const checkStreak = useCallback(async () => {
    try {
      const s = await api<ChoreStreak>('/api/chores/streak');
      if (!streakPrimed.current) {
        streakPrimed.current = true;
        seenClearAt.current = s.lastClearAt;
        return;
      }
      if (s.lastClearAt != null && s.lastClearAt !== seenClearAt.current) {
        seenClearAt.current = s.lastClearAt;
        setStreakLabel(
          s.streak > 1
            ? `Whole list clear — ${s.streak} weeks running.`
            : 'Whole list clear — the streak starts here.',
        );
      }
    } catch (e) {
      console.error('[Pear] streak read failed', e);
    }
  }, []);

  // Prime the ref before the first tick can race it.
  useEffect(() => {
    void checkStreak();
  }, [checkStreak]);

  // A chore deleted while its chooser or options card is open must not leave
  // a control pointing at a row that no longer exists.
  useEffect(() => {
    if (items === null) return;
    if (capped && !items.some(c => c.choreId === capped)) setCapped(null);
    if (laterFor && !items.some(c => c.choreId === laterFor)) setLaterFor(null);
  }, [items, capped, laterFor]);

  /** Snooze to a chosen time, or defer to the next natural slot. The 4th push
   *  in an occurrence is refused by the server with snooze_cap, and THAT is
   *  what opens the options card — the cap is enforced where the data is, so
   *  two devices cannot each take three pushes. */
  const pushLater = async (chore: ChoreItem, kind: 'snooze' | 'defer', returnAt?: number) => {
    if (busyId) return;
    setBusyId(chore.choreId);
    try {
      await api(`/api/chores/${chore.choreId}/${kind}`, {
        body: kind === 'snooze' ? { returnAt } : {},
      });
      setLaterFor(null);
      setLaterNote(null);
      await reload();
    } catch (e) {
      if (errCode(e) === 'snooze_cap') {
        setCapped(chore.choreId);
        setLaterFor(null);
        setLaterNote(null);
      } else {
        setLaterNote('Could not move that one — try again.');
      }
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  /** Second half of the cap: renegotiate turns the stuck chore into an ask,
   *  which is where a disagreement about whose job it is belongs — the Life
   *  lane, where it can be accepted, countered or declined (card xvom08w4BgGT). */
  const renegotiate = async (chore: ChoreItem) => {
    if (busyId) return;
    setBusyId(chore.choreId);
    try {
      await api('/api/requests', {
        body: { text: `Can we sort out who does “${chore.title}”?`, lane: 'life' },
      });
      setCapped(null);
      setLaterNote('Asked your partner — it is sitting in Needs you.');
    } catch (e) {
      console.error('[Pear] renegotiate request failed', e);
      setLaterNote('Could not send that ask — try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** Who the row is on — the words the Done row keeps and the toast reads
   *  back (card 4kQn1aaw0rf9). Mode-blind on purpose: a finished round is a
   *  record of who was on it, not of the rule that put them there. */
  const assigneeLabel = (chore: ChoreItem): string => {
    if (chore.assigneeId == null) return 'Anyone';
    if (me && chore.assigneeId === me.memberId) return 'Me';
    return 'Partner';
  };

  /** What the live pill says (card UeOwZSHfdZCh): a claim chore reads
   *  "Claim" while nobody has taken it, and an alternate one points at who
   *  is NEXT — the pointer the last completion handed over. */
  const pillLabel = (chore: ChoreItem): string => {
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'claim' && chore.assigneeId == null) return 'Claim';
    if (mode === 'alternate' && chore.assigneeId != null) return `Next: ${assigneeLabel(chore)}`;
    return assigneeLabel(chore);
  };

  /** What the pill tells a screen reader about the tap it offers. */
  const pillAria = (chore: ChoreItem): string => {
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'claim') {
      if (chore.assigneeId == null) return `Claim ${chore.title}`;
      return chore.assigneeId === (me?.memberId ?? '')
        ? `Claimed by you — tap to let go of ${chore.title}`
        : `Claimed by your partner — ${chore.title}`;
    }
    return `Assigned to ${pillLabel(chore)}. Tap to reassign.`;
  };

  /** What the celebration says (card UeOwZSHfdZCh): the two modes that move
   *  the assignment on completion must name the DOER, because the row they
   *  read back is already about the next round — "Claim" and "Next: …" are
   *  not what a toast should answer "who did it?" with. */
  const toastLabel = (chore: ChoreItem | null): string => {
    if (!chore) return 'Chore done';
    const mode = chore.assignMode ?? 'fixed';
    if (mode === 'fixed') return `Chore done — ${assigneeLabel(chore)}`;
    const doer = chore.completedBy == null ? null
      : chore.completedBy === (me?.memberId ?? '') ? 'Me' : 'Partner';
    return `Chore done — ${doer ?? assigneeLabel(chore)}`;
  };

  const open = items?.filter(c => c.status === 'open') ?? [];
  const done = items?.filter(c => c.status === 'done') ?? [];

  /* The week the drawer shows: everything due before the end of tomorrow's
     seven-day window, overdue rows included — a late chore is exactly what
     the at-a-glance view exists to surface. Undated chores have no day to
     hang on, so they stay out of the table. */
  const DAY_MS = 24 * 3_600_000;
  const weekStart = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  })();
  const weekRows = (items ?? [])
    .filter(c => c.dueAt != null && c.dueAt < weekStart + 7 * DAY_MS)
    .sort((a, b) => (a.dueAt ?? 0) - (b.dueAt ?? 0));
  const weekDayKey = (c: ChoreItem): string =>
    c.dueAt == null ? '' : new Date(c.dueAt).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const weekDays = [...new Set(weekRows.map(weekDayKey))];
  const weekFiltered = weekRows.filter(c => {
    if (weekDay !== 'all' && weekDayKey(c) !== weekDay) return false;
    if (weekWho !== 'all' && assigneeLabel(c) !== weekWho) return false;
    if (weekDone !== 'all' && c.status !== weekDone) return false;
    return true;
  });
  /** The chore the toast is celebrating — its assignment is what the toast
   *  reads back, so "Anyone" / "Me" / "Partner" is honoured after tick-off
   *  instead of a bare "Chore done" (card 4kQn1aaw0rf9). */
  const celebratedChore = items?.find(c => c.choreId === celebrated) ?? null;
  /** The chore the cap stopped — null once it is deleted or dismissed, which
   *  is exactly when the options card should stop rendering. */
  const cappedChore = items?.find(c => c.choreId === capped) ?? null;

  /** A clock with no day to hang on cannot be stored, so Add waits for the
   *  date instead of silently dropping the time the user just picked. The
   *  date field is the only one of the two that can stand alone. */
  const timeWithoutDate = time !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(due);

  /* The week's body — the three filters and the day | task | responsible |
     complete table (or its empty hint) — is shared verbatim by the drawer
     below the token and the rail at it (desktop uplift A6, card
     R0Yth1PtWGRg). Exactly ONE of the two is rendered per width, so the
     testids below never appear twice and a walk can never resolve a filter
     to a hidden copy (the contract InboxList ships). */
  const weekBody = (
    <>
      <div className="ch-week-filters" role="group" aria-label="Filter the week">
        <label className="ch-week-filter">
          <span>Day</span>
          <select
            className="pear-chat-input"
            data-testid="ch-week-filter-day"
            value={weekDay}
            onChange={e => setWeekDay(e.target.value)}
          >
            <option value="all">All days</option>
            {weekDays.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label className="ch-week-filter">
          <span>Responsible</span>
          <select
            className="pear-chat-input"
            data-testid="ch-week-filter-who"
            value={weekWho}
            onChange={e => setWeekWho(e.target.value)}
          >
            <option value="all">Everyone</option>
            <option value="Me">Me</option>
            <option value="Partner">Partner</option>
            <option value="Anyone">Anyone</option>
          </select>
        </label>
        <label className="ch-week-filter">
          <span>Complete</span>
          <select
            className="pear-chat-input"
            data-testid="ch-week-filter-done"
            value={weekDone}
            onChange={e => setWeekDone(e.target.value)}
          >
            <option value="all">All</option>
            <option value="open">Not done</option>
            <option value="done">Done</option>
          </select>
        </label>
      </div>

      {weekFiltered.length === 0 ? (
        <p className="rq-empty-hint ch-week-empty" data-testid="ch-week-empty">
          Nothing matches — the week is clear on that filter.
        </p>
      ) : (
        <table className="ch-week-table" data-testid="ch-week-table">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Task</th>
              <th scope="col">Responsible</th>
              <th scope="col">Complete</th>
            </tr>
          </thead>
          <tbody>
            {weekFiltered.map(c => (
              <tr key={c.choreId} data-testid="ch-week-row" className={c.status === 'done' ? 'is-done' : ''}>
                <td>{weekDayKey(c)}</td>
                <td>{c.title}</td>
                <td>{assigneeLabel(c)}</td>
                <td>
                  {c.status === 'done' ? (
                    <span className="ch-week-done" aria-label="Done">✓</span>
                  ) : (
                    <span className="ch-week-todo" aria-label="Not done">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  return (
    <section className="ch-panel" id="life-chores-panel" aria-labelledby="ch-title">
      <h2
        id="ch-title"
        className="ch-title"
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'var(--type-h3-size)',
          letterSpacing: 'var(--type-tracking-wider)',
          fontWeight: 400,
          color: 'var(--color-cream)',
        }}
      >
        Chores
      </h2>

      {/* Desktop uplift A6 (card R0Yth1PtWGRg) — Chores: list + week strip.
          Below the token both wrappers are display:contents, so the phone
          box tree is byte-for-byte the one that shipped; at the token the
          grid puts the list left and the week rail right. */}
      <div className={wide ? 'ch-split is-split' : 'ch-split'}>
        <div className="ch-split-main">
      {/* Card QR9phRSRQHNx — the week at a glance: day | task | responsible |
          complete, in a drawer so the list underneath is untouched. Desktop
          uplift A6 (R0Yth1PtWGRg): at the >=1100px token there is no drawer
          to open — the rail beside the list IS the week — so the toggle
          retires there instead of opening a second copy of the same table. */}
      {!wide && (
        <button
          type="button"
          className="pear-btn ch-week-toggle"
          id="ch-week-toggle"
          data-testid="ch-week-toggle"
          aria-haspopup="dialog"
          aria-expanded={weekOpen}
          onClick={() => setWeekOpen(true)}
        >
          This week
        </button>
      )}

      <form className="rq-card ch-composer" onSubmit={submitChore}>
        <label className="rq-composer-label" htmlFor="ch-input">
          {editing ? 'Edit chore' : 'Add a chore'}
        </label>
        <input
          id="ch-input"
          type="text"
          className="pear-chat-input rq-input"
          placeholder="e.g. bins out on Tuesday"
          aria-label="New chore"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        {/* Which rule owns the assignee (card UeOwZSHfdZCh): fixed keeps who
            you pick, claim is taken by whoever taps first, alternate passes
            to the partner on every completion. */}
        <div className="ch-composer-row" role="group" aria-label="Who decides">
          {ASSIGN_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={assignMode === opt.value}
              className={`rq-repeat-chip${assignMode === opt.value ? ' is-on' : ''}`}
              onClick={() => { setAssignMode(opt.value); setAssignModePicked(true); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="rq-empty-hint ch-assign-hint" id="ch-assign-hint">
          {ASSIGN_HINT[assignMode]}
        </p>

        {/* Who is on it — decided when the chore is created, so the row it
            lands in is already assigned (card eudAJs92EZ9B). The partner
            segment only exists once a second member has joined. Hidden while
            the mode is Claim: nobody is on a claim chore until they take it,
            so there is nothing here to pick (card UeOwZSHfdZCh). */}
        {assignMode !== 'claim' && (
        <div className="ch-composer-row" role="group" aria-label="Who is on it">
          <button
            type="button"
            aria-pressed={assignee === 'me'}
            className={`rq-repeat-chip${assignee === 'me' ? ' is-on' : ''}`}
            onClick={() => setAssignee('me')}
          >
            Me
          </button>
          {otherMember && (
            <button
              type="button"
              aria-pressed={assignee === 'partner'}
              className={`rq-repeat-chip${assignee === 'partner' ? ' is-on' : ''}`}
              onClick={() => setAssignee('partner')}
            >
              {partnerName}
            </button>
          )}
          <button
            type="button"
            aria-pressed={assignee === 'anyone'}
            className={`rq-repeat-chip${assignee === 'anyone' ? ' is-on' : ''}`}
            onClick={() => setAssignee('anyone')}
          >
            Anyone
          </button>
        </div>
        )}

        <div className="ch-composer-row" role="group" aria-label="Cadence">
          {CADENCE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={cadence === opt.value}
              className={`rq-repeat-chip${cadence === opt.value ? ' is-on' : ''}`}
              onClick={() => {
                setCadence(opt.value);
                // A NEW recurring chore takes turns by default (card
                // kZX3gsJkKaTH): the roll-forward already alternates on
                // completion, so the pair never needs a fairness reading to
                // stay even. A one-off has no next round to hand over, so it
                // stays Fixed. Anyone who has picked a mode keeps it.
                if (!assignModePicked) setAssignMode(opt.value === 'recurring' ? 'alternate' : 'fixed');
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {cadence === 'recurring' && (
          <>
            {/* Which rule (card VmoORelj1HuO): a cadence, a straight N-day
                interval, or a step counted from the tick. */}
            <div className="ch-composer-row" role="group" aria-label="Repeats how">
              {RECUR_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={recurMode === opt.value}
                  className={`rq-repeat-chip${recurMode === opt.value ? ' is-on' : ''}`}
                  onClick={() => setRecurMode(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {recurMode === 'fixed' && (
              <div className="ch-composer-row" role="group" aria-label="Repeats">
                {FREQ_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={freq === opt.value}
                    className={`rq-repeat-chip${freq === opt.value ? ' is-on' : ''}`}
                    onClick={() => setFreq(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {recurMode !== 'fixed' && (
              <div className="ch-composer-row ch-every-row" role="group" aria-label="Step in days">
                <label className="ch-due-label" htmlFor="ch-every">
                  {recurMode === 'interval' ? 'Every' : 'Days after it is done'}
                </label>
                <input
                  id="ch-every"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={365}
                  className="pear-chat-input ch-every"
                  aria-label={recurMode === 'interval' ? 'Interval in days' : 'Days after completion'}
                  value={every}
                  onChange={e => setEvery(e.target.value)}
                />
                <span className="rq-empty-hint">
                  {recurMode === 'interval'
                    ? 'days, kept on schedule'
                    : 'days after the last tick'}
                </span>
              </div>
            )}
          </>
        )}

        <div className="ch-composer-foot">
          {/* One field = one label stacked on its own picker (Jonathan
              2026-10-20). Label and input used to be flat siblings of the whole
              row, so a wrap could leave "Specify time" on one line with its
              time picker on the next, and Add hanging against a label instead
              of a field. */}
          <div className="ch-field">
            <label className="ch-due-label" htmlFor="ch-due">
              {cadence === 'recurring' ? 'First due' : 'Due'}
            </label>
            <input
              id="ch-due"
              type="date"
              className="pear-chat-input ch-due"
              aria-label="Due date"
              value={due}
              onChange={e => setDue(e.target.value)}
            />
          </div>
          <div className="ch-field">
            {/* Specify Time (card eudAJs92EZ9B) — a clock next to the date.
                Always live: gating it on the date first meant a tap on the
                clock did nothing, so nobody could set a time (founder report
                2026-10-08). Add takes the date requirement instead. */}
            <label className="ch-due-label" htmlFor="ch-time">
              Specify time
            </label>
            <input
              id="ch-time"
              type="time"
              className="pear-chat-input ch-due ch-time"
              aria-label="Specify time"
              aria-describedby={timeWithoutDate ? 'ch-time-hint' : undefined}
              value={time}
              onChange={e => setTime(e.target.value)}
            />
          </div>
          {timeWithoutDate && (
            <span className="rq-empty-hint ch-time-hint" id="ch-time-hint">
              Pick a date for that time.
            </span>
          )}
          <button
            type="submit"
            className="pear-btn pear-btn-mustard ch-add"
            disabled={adding || title.trim().length === 0 || timeWithoutDate
              || (cadence === 'recurring' && recurMode !== 'fixed' && everyDays === null)}
          >
            {adding ? (editing ? 'Saving…' : 'Adding…') : editing ? 'Save' : 'Add'}
          </button>
          {editing && (
            <button
              type="button"
              id="ch-cancel"
              className="pear-btn ch-cancel"
              disabled={adding}
              onClick={resetComposer}
            >
              Cancel
            </button>
          )}
        </div>

        {/* Add to Calendar & Tasks (card eudAJs92EZ9B) — the switch the card
            asks for at creation, wired to the OAuth shipped in hWnpJn7Tfl38.
            Only rendered on builds that carry a Google client, so the panel
            never offers a switch that cannot do anything. */}
        {google?.configured && (
          <div className="ch-composer-row ch-sync-row" id="ch-sync-row">
            <span className="rq-composer-label">Add to Calendar &amp; Tasks</span>
            <Switch
              checked={addToGoogle}
              onChange={setAddToGoogle}
              label="Add to Calendar and Tasks"
            />
            {!google.connected && (
              <span className="rq-empty-hint ch-sync-hint">
                Link Google below and it goes across.
              </span>
            )}
          </div>
        )}
      </form>

      {/* Seed library (card VmoORelj1HuO): forty common chores, grouped,
          multi-pick — tap several, add them together, and the composer
          above stays what it was for the one you are actually typing. */}
      <div className="rq-card ch-seed-card" id="ch-seed-library">
        <div className="ch-seed-head">
          <span className="rq-composer-label">Common chores</span>
          <span className="rq-empty-hint ch-seed-count" id="ch-seed-count" role="status">
            {picked.length === 0 ? 'Tap a few, then add them together.' : `${picked.length} picked`}
          </span>
          {picked.length > 0 && (
            <button type="button" id="ch-seed-clear" className="pear-btn ch-seed-clear" onClick={() => setPicked([])}>
              Clear
            </button>
          )}
        </div>
        <div className="ch-seeds" role="group" aria-label="Common chores">
          {CHORE_SEED_GROUPS.map(group => (
            <div className="ch-seed-group" key={group.group}>
              <span className="ch-seed-group-label">{group.group}</span>
              <div className="ch-seed-group-items">
                {group.titles.map(seed => (
                  <button
                    key={seed}
                    type="button"
                    aria-pressed={picked.includes(seed)}
                    className={`rq-repeat-chip ch-seed${picked.includes(seed) ? ' is-on' : ''}`}
                    onClick={() => toggleSeed(seed)}
                  >
                    {seed}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          id="ch-seed-add"
          className="pear-btn pear-btn-mustard ch-seed-add"
          disabled={picked.length === 0 || adding}
          onClick={() => void addSeeds()}
        >
          {adding ? 'Adding…' : picked.length > 0 ? `Add ${picked.length}` : 'Add'}
        </button>
      </div>

      {/* Lifecycle (card xvom08w4BgGT): the streak celebration, the cap's
          options card, and the chooser's feedback — all about the LIST, so
          they sit above it rather than inside any one row. */}
      {streakLabel && (
        <div className="ch-streak" id="ch-streak" role="status">
          <span className="ch-streak-text">{streakLabel}</span>
          <Pressable
            className="pear-btn ch-streak-hide"
            aria-label="Hide the list-clear streak notice"
            onClick={() => setStreakLabel(null)}
          >
            Hide
          </Pressable>
        </div>
      )}

      {cappedChore && (
        <div className="rq-card ch-cap" id="ch-cap" role="group" aria-label={`Three moves already used on ${cappedChore.title}`}>
          <p className="rq-composer-label">Three moves already used</p>
          <p className="rq-empty-hint">
            {cappedChore.title} has been put off three times this round — change who is on it or talk it through, instead of moving it again.
          </p>
          <div className="ch-cap-actions">
            <Pressable
              className="pear-btn"
              id="ch-cap-reassign"
              disabled={busyId != null}
              onClick={() => { setCapped(null); void tapAssignee(cappedChore); }}
            >
              Reassign
            </Pressable>
            <Pressable
              className="pear-btn pear-btn-mustard"
              id="ch-cap-renegotiate"
              disabled={busyId != null}
              onClick={() => void renegotiate(cappedChore)}
            >
              Renegotiate
            </Pressable>
          </div>
        </div>
      )}

      {laterNote && <p className="rq-empty-hint ch-later-note" id="ch-later-note" role="status">{laterNote}</p>}

      <div className="rq-list ch-list">
        {items === null && !failed && <SkeletonBlock lines={3} />}
        {failed && <p className="rq-empty">Could not load chores.</p>}
        {items !== null && items.length === 0 && !failed && (
          <div className="rq-empty-state" id="ch-seed-state">
            <p className="rq-empty-title">No chores yet.</p>
            <p className="rq-empty-hint">Add one — bins, a clean, a shop — and pick who is on it.</p>
            {/* Entry state: the library above IS the start. Its chips are
                multi-pick, so the first shelf of chores is one tap each and
                a single Add. */}
            <p className="rq-empty-hint">Or pick a few from Common chores above.</p>
          </div>
        )}

        {open.map(chore => {
          const busy = busyId === chore.choreId;
          return (
            <div className="rq-row ch-row" key={chore.choreId} data-chore-id={chore.choreId}>
              <div className="rq-head">
                <span className={`rq-state ${chore.cadence === 'recurring' ? 'is-countered' : 'is-pending'}`}>
                  {recurChip(chore)}
                </span>
                <span className="rq-when">{dueLabel(chore)}</span>
              </div>

              <p className="pear-request-text rq-text">{chore.title}</p>

              <div className="ch-row-foot">
                <Pressable
                  className="pear-btn ch-assignee"
                  disabled={busy}
                  aria-label={pillAria(chore)}
                  onClick={() => void tapAssignee(chore)}
                >
                  {pillLabel(chore)}
                </Pressable>
                <Pressable
                  className="pear-btn pear-btn-mustard ch-done"
                  disabled={busy}
                  aria-label={`Mark ${chore.title} done`}
                  onClick={() => void complete(chore)}
                >
                  {busy ? '…' : 'Tick off'}
                </Pressable>
                {/* A receipt with the tick (card MnorjNDwB3of): opens the one
                    line composer; saving it also completes the chore. */}
                <Pressable
                  className="pear-btn ch-note-open"
                  disabled={busy}
                  aria-label={`Add a done note to ${chore.title}`}
                  aria-expanded={noteFor === chore.choreId}
                  onClick={() => (noteFor === chore.choreId ? (setNoteFor(null), setNoteDraft('')) : openNote(chore))}
                >
                  Note
                </Pressable>
                {/* Edit loads the row into the composer above; Delete arms on
                    the first tap and removes on the second (card VmoORelj1HuO). */}
                <Pressable
                  className="pear-btn ch-edit"
                  disabled={busy}
                  aria-label={`Edit ${chore.title}`}
                  onClick={() => beginEdit(chore)}
                >
                  Edit
                </Pressable>
                <Pressable
                  className={`pear-btn ch-delete${armedDelete === chore.choreId ? ' is-armed' : ''}`}
                  disabled={busy}
                  aria-label={armedDelete === chore.choreId ? `Confirm delete ${chore.title}` : `Delete ${chore.title}`}
                  aria-pressed={armedDelete === chore.choreId}
                  onClick={() => void removeChore(chore)}
                >
                  {armedDelete === chore.choreId ? 'Sure?' : 'Delete'}
                </Pressable>
                {/* Later opens the lifecycle chooser: three snoozes and a
                    defer, all judged by the cap on the server (xvom08w4BgGT). */}
                <Pressable
                  className="pear-btn ch-later"
                  disabled={busy}
                  aria-label={`Later — snooze or defer ${chore.title}`}
                  aria-expanded={laterFor === chore.choreId}
                  onClick={() => {
                    setLaterFor(laterFor === chore.choreId ? null : chore.choreId);
                    setLaterNote(null);
                  }}
                >
                  Later
                </Pressable>
              </div>

              {/* Reactions + the one-line receipt composer (MnorjNDwB3of). */}
              {renderReactions(chore)}
              {renderNoteComposer(chore)}

              {laterFor === chore.choreId && (
                <div className="ch-later-panel" id="ch-later-panel" role="group" aria-label={`Move ${chore.title} later`}>
                  <span className="ch-later-label">Come back…</span>
                  <div className="ch-later-choices">
                    {SNOOZE_CHOICES.map(choice => (
                      <Pressable
                        key={choice.key}
                        className="rq-repeat-chip ch-later-chip"
                        disabled={busy}
                        aria-label={`Snooze ${chore.title} ${choice.label.toLowerCase()}`}
                        onClick={() => void pushLater(chore, 'snooze', Date.now() + choice.ms)}
                      >
                        {choice.label}
                      </Pressable>
                    ))}
                    <Pressable
                      className="rq-repeat-chip ch-later-chip"
                      disabled={busy}
                      aria-label={`Defer ${chore.title} to its next due date`}
                      onClick={() => void pushLater(chore, 'defer')}
                    >
                      Defer to next due
                    </Pressable>
                  </div>
                  <p className="rq-empty-hint ch-later-count" id="ch-later-count" role="status">
                    {chore.snoozeCount >= 3
                      ? 'Three moves used this round — the next one needs a change of plan.'
                      : `${chore.snoozeCount} of 3 moves used this round.`}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {done.length > 0 && (
          <>
            <p className="ch-done-heading">Done</p>
            {done.map(chore => (
              <div className="rq-row ch-row is-done" key={chore.choreId} data-chore-id={chore.choreId}>
                <div className="rq-head">
                  <span className="rq-state is-accepted">Done</span>
                  <span className="rq-when">
                    {chore.completedAt ? new Date(chore.completedAt).toLocaleDateString() : ''}
                  </span>
                </div>
                <p className="pear-request-text rq-text">{chore.title}</p>
                <div className="ch-row-foot">
                  <span className="ch-assignee is-static">{assigneeLabel(chore)}</span>
                  {/* A closed row still takes a receipt (card MnorjNDwB3of):
                      either member can leave or replace the line, and react
                      to the finished work. */}
                  <Pressable
                    className="pear-btn ch-note-open"
                    disabled={busyId === chore.choreId}
                    aria-label={`Add a done note to ${chore.title}`}
                    aria-expanded={noteFor === chore.choreId}
                    onClick={() => (noteFor === chore.choreId ? (setNoteFor(null), setNoteDraft('')) : openNote(chore))}
                  >
                    Note
                  </Pressable>
                </div>
                {renderReactions(chore)}
                {renderNoteComposer(chore)}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Google Calendar + Tasks (card hWnpJn7Tfl38) — the link the Chores
          panel offers before any write can happen. Hidden entirely when this
          worker has no Google client, so the panel never shows a button that
          cannot work. */}
      <div className="rq-card ch-google" id="ch-google">
        <p className="rq-composer-label">Google Calendar &amp; Tasks</p>
        <p className="rq-empty-hint ch-google-status" id="ch-google-status">
          {google === null && 'Checking the Google link…'}
          {google !== null && !google.configured && 'Not linked on this build — a Google connection is not configured here.'}
          {google?.configured && !google.connected &&
            'Sign in with the account in which you’d like the task reminders to appear — a chore then lands on that account’s calendar and task list.'}
          {google?.connected &&
            `Connected${google.accountEmail ? ` as ${google.accountEmail}` : ''} — ${google.scopes.includes('https://www.googleapis.com/auth/calendar') ? 'calendar and tasks' : 'tasks'} granted.`}
        </p>
        {gNote && <p className="rq-empty-hint ch-google-note" id="ch-google-note" role="status">{gNote}</p>}
        {google?.configured && (google.connected ? (
          <Pressable
            className="pear-btn ch-google-btn"
            id="ch-google-disconnect"
            disabled={gBusy}
            onClick={() => void disconnectGoogle()}
          >
            {gBusy ? '…' : 'Disconnect Google'}
          </Pressable>
        ) : (
          <Pressable
            className="pear-btn pear-btn-mustard ch-google-btn"
            id="ch-google-connect"
            disabled={gBusy}
            onClick={() => void connectGoogle()}
          >
            {gBusy ? '…' : 'Connect Google'}
          </Pressable>
        ))}
      </div>

        </div>

        {/* The rail (A6): the SAME weekBody the drawer would have shown —
            filters and table verbatim — standing beside the list. Layout,
            not an overlay: no backdrop, no Esc, no focus trap; it is simply
            where the week lives now. */}
        {wide && (
          <aside
            className="ch-week-rail"
            data-testid="ch-week-rail"
            aria-label="This week"
            tabIndex={-1}
          >
            <div className="ch-week-head">
              <h3 className="ch-week-title" id="ch-week-rail-title">
                This week
              </h3>
            </div>
            {weekBody}
          </aside>
        )}
      </div>

      {/* Week summary drawer (card QR9phRSRQHNx) — day | task | responsible |
          complete, filterable on any of the three. Escape or the backdrop
          closes it, the same contract every other drawer in the app keeps.
          Below the A6 token only: at the token the rail above carries the
          same body, so rendering both would put every filter testid in the
          DOM twice. */}
      <Overlay
        open={!wide && weekOpen}
        onClose={() => setWeekOpen(false)}
        className="ch-week-overlay"
        panelClassName="ch-week-drawer"
        labelledBy="ch-week-title"
        panelTestId="ch-week-drawer"
      >
        <div className="ch-week-head">
          <h3 className="ch-week-title" id="ch-week-title">
            This week
          </h3>
          <button
            type="button"
            className="pear-btn ch-week-close"
            aria-label="Close week summary"
            onClick={() => setWeekOpen(false)}
          >
            ✕
          </button>
        </div>

        {weekBody}
      </Overlay>

      {/* Same celebration primitive the kudos card ships (HBYCnnD9q5FS) —
          reused, not re-drawn, so both surfaces celebrate identically. The
          kudos glyph stays with kudos: a chore tick-off reads as its own
          line, not a sparkle (founder report 2026-10-08). */}
      {celebrated && (
        <KudosToast
          glyph=""
          variant="shimmer"
          label={toastLabel(celebratedChore)}
        />
      )}
    </section>
  );
}
